/**
 * Subscription Purchase Context
 * 
 * React Context for managing subscription purchase flow state.
 * 
 * **Purpose**: Store user selections and payment data during the subscription
 * purchase flow (plan selection, payment, guest account creation).
 * 
 * **Scope**: Should only be mounted on subscription purchase routes.
 * 
 * **Similar to**: BookingContext pattern
 * 
 * **Note**: This file contains types, context creation, and the hook.
 * The provider component is in SubscriptionPurchaseProvider.tsx to satisfy Fast Refresh.
 * 
 * @module subscription/context
 */

import { createContext, useContext } from 'react';
import type { FormattedSubscriptionPlan } from '../types';
import type { User } from '~/services/auth/types';

/**
 * Payment provider type
 */
export type PaymentProvider = 'stripe' | 'square' | null;

/**
 * Tab selection for subscriptions
 */
export type SubscriptionTab = 'memberships' | 'passes';

/**
 * Subscription Purchase State
 */
export interface SubscriptionPurchaseState {
  // Selection
  /** Currently selected subscription plan */
  selectedSubscription: FormattedSubscriptionPlan | null;
  
  /** Active tab (memberships or passes) */
  selectedTab: SubscriptionTab;
  
  // Payment flow (mirrors booking pattern)
  /** User from guest creation or authentication (full User object with id, status, etc.) */
  guestUser: User | null;
  
  /** Payment method database ID (payment_methods.id) */
  paymentMethodId: number | null;
  
  /** Payment provider being used */
  paymentProvider: PaymentProvider;
  
  /** Tax rate from club settings (as decimal, e.g., 0.25 for 25%) */
  taxRate: number;

  /**
   * Promo code captured from `?promo=` (7a) — one claim for the whole funnel,
   * owned by the provider via `usePromoCodeCapture`. Null when none captured
   * or after `clearPromoCode`.
   */
  promoCode: string | null;

  /**
   * How many passes the order buys (gifting). Captured input — written only by
   * the stepper's handlers. NOT clamped to a promo's ceiling here: that ceiling
   * is server state (the quote's), and mirroring it into this value is the
   * copy-the-server-answer bug the react-state convention forbids. An over-cap
   * quantity surfaces as the quote's own refusal, with a one-click fix.
   */
  quantity: number;

  /**
   * The buyer keeps NONE of the passes — every one is a gift.
   *
   * Captured input, like `quantity`: the "this is a gift" checkbox writes it.
   * It changes only how many recipients the order needs (`recipientsNeeded`),
   * never the price — the quote has always been driven by quantity alone, so a
   * gifted pass costs exactly what a kept one does.
   *
   * **`null` means "not chosen", and is not the same as `false`.** A buyer who
   * already holds a MEMBERSHIP gets the box ticked for them but may untick it,
   * so "they unticked" has to be distinguishable from "they never touched it" —
   * otherwise the default re-asserts itself and the box springs back. A buyer
   * who holds a PASS is locked true at render instead; that lock is read from
   * server state and never written here.
   */
  giftAll: boolean | null;

  /**
   * Who the passes go to, by position. `recipients[0]` is pass #2 normally, or
   * pass #1 when `giftAll` is set.
   * May be LONGER than the order needs: lowering the quantity keeps what was
   * typed, so raising it again (or a dropped promo) loses nothing.
   * Senders slice to `recipientsNeeded(quantity, giftAll)`.
   */
  recipients: GiftRecipientDraft[];
}

/** A gift recipient as typed — one name field, as the checkout design draws it. */
export interface GiftRecipientDraft {
  name: string;
  email: string;
}

/**
 * Subscription Purchase Actions
 */
export interface SubscriptionPurchaseActions {
  /** Set the selected subscription plan */
  setSelectedSubscription: (subscription: FormattedSubscriptionPlan | null) => void;
  
  /** Set the active tab */
  setSelectedTab: (tab: SubscriptionTab) => void;
  
  /** Set guest user (full User object from /users/guest response) */
  setGuestUser: (user: User | null, isAuthenticated?: boolean) => void;
  
  /** Set payment method ID and provider */
  setPaymentMethodId: (id: number | null, provider: PaymentProvider) => void;
  
  /** Set tax rate from club settings */
  setTaxRate: (rate: number) => void;
  
  /** Clear all purchase state (after successful purchase or cancellation) */
  clearPurchase: () => void;

  /**
   * Capture a manually-entered promo code (the checkout steps' input field,
   * D21 amendment) — same session-scoped stash as a `?promo=` link claim.
   */
  setPromoCode: (code: string) => void;

  /**
   * Forget the captured promo code (storage + state). Called on successful
   * purchase, on a `promotion_rejected` create failure (auto-drop: the
   * next quote reprices at list), and by the input's remove affordance.
   */
  clearPromoCode: () => void;

  /** Set the order quantity (≥ 1). */
  setQuantity: (quantity: number) => void;

  /** Give every pass away, keeping none. */
  setGiftAll: (giftAll: boolean) => void;

  /** Patch one recipient by position, growing the list as needed. */
  setRecipient: (index: number, patch: Partial<GiftRecipientDraft>) => void;
}

/**
 * Combined context value
 */
export interface SubscriptionPurchaseContextValue 
  extends SubscriptionPurchaseState, 
          SubscriptionPurchaseActions {}

/**
 * Subscription Purchase Context
 * 
 * Provides purchase state and actions to all child components.
 */
export const SubscriptionPurchaseContext = createContext<SubscriptionPurchaseContextValue | null>(null);

/**
 * Hook to access subscription purchase context
 * 
 * **Must be used within SubscriptionPurchaseProvider**
 * 
 * @throws Error if used outside provider
 * 
 * @example
 * ```tsx
 * function SubscriptionCard() {
 *   const { selectedSubscription, setSelectedSubscription } = useSubscriptionPurchase();
 *   
 *   return (
 *     <button onClick={() => setSelectedSubscription(plan)}>
 *       Select {plan.name}
 *     </button>
 *   );
 * }
 * ```
 */
export function useSubscriptionPurchase(): SubscriptionPurchaseContextValue {
  const context = useContext(SubscriptionPurchaseContext);
  
  if (!context) {
    throw new Error(
      'useSubscriptionPurchase must be used within a SubscriptionPurchaseProvider. ' +
      'Make sure the provider is mounted in your subscription purchase routes.'
    );
  }
  
  return context;
}

